import { ProCard, ProTable } from '@ant-design/pro-components';
import { useQuery } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import {
    Avatar,
    Button,
    Checkbox,
    DatePicker,
    Flex,
    Form,
    Input,
    Popconfirm,
    Select,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import { get, isEqual, mapValues } from 'lodash';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * APIs
 */
import { dUser, users, uUser } from '@/apis/user';

/**
 * Components
 */
import ComponentCreateUpdate from './create_update';

/**
 * Types
 */
import type { QueryUserDto, UserWithRoleUsersDto } from '@/dtos/user.dto';
import type { ActionType, ProTableProps } from '@ant-design/pro-components';
import type { FormProps, PaginationProps } from 'antd';
import type { Action } from './create_update';

/**
 * Constants
 */
import { RANGE_PICKER_PRESETS } from '@/constants/presets';

const defaultPayload: QueryUserDto = {
    id: undefined,
    email_fuzzy: undefined,
    mobile_fuzzy: undefined,
    nickname: undefined,
    username: undefined,
    is_deleted: undefined,
    is_disabled: undefined,
    created_at: undefined,
};

const PageUsers: React.FC = () => {
    /**
     * Hooks
     */
    const [formSearch] = Form.useForm();

    const tableRef = useRef<ActionType>();

    const updateRef = useRef<Action>();

    const { t } = useTranslation();

    /**
     * States
     */
    const [loading, setLoading] = useSetState<Record<string, boolean>>({});

    /**
     * Paginations
     */
    const [pagination, setPagination] = useSetState<PaginationProps>({
        current: 1,
        pageSize: 20,
        showQuickJumper: true,
        showSizeChanger: true,
        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
    });

    /**
     * Payloads
     */
    const [payload, setPayload] = useSetState<QueryUserDto>({
        ...defaultPayload,
    });

    /**
     * Requests
     */
    const {
        data: dataUsers,
        isLoading: loadingUsers,
        refetch: refetchUsers,
    } = useQuery({
        queryKey: ['users', pagination.current, pagination.pageSize, payload],
        retry: false,
        queryFn: () => users({ page: pagination.current, size: pagination.pageSize, ...payload }),
    });

    /**
     * ChildrenProps
     */
    const formSearchProps: FormProps = {
        form: formSearch,
        initialValues: {
            ...defaultPayload,
            key: 'id',
            value: undefined,
        },
        layout: 'inline',
        onFinish: (values) => {
            if (values.value) {
                values[values.key] = values.value;
            }

            delete values.key;
            delete values.value;

            values.created_at = [
                values.created_at?.[0].startOf('day').format(),
                values.created_at?.[1].endOf('day').format(),
            ];

            const assignPayload = {
                ...defaultPayload,
                ...mapValues(values, (value) => (value === '' ? undefined : value)),
            };

            if (isEqual(payload, assignPayload)) {
                return refetchUsers();
            }

            setPagination({
                current: 1,
            });

            setPayload(assignPayload);
        },
    };

    const proTableProps: ProTableProps<UserWithRoleUsersDto, QueryUserDto> = {
        actionRef: tableRef,
        bordered: true,
        columns: [
            {
                dataIndex: 'avatar',
                title: t('user'),
                width: 400,
                render: (_, entity) => (
                    <Space align="center">
                        <Avatar alt="" src={entity.avatar && `${import.meta.env.VITE_CDN}/${entity.avatar}`}>
                            {entity.nickname?.charAt(0).toUpperCase()}
                        </Avatar>

                        <Typography.Text copyable={{ text: entity.id }}>
                            {entity.nickname} (@{entity.username})
                        </Typography.Text>
                    </Space>
                ),
            },
            {
                dataIndex: 'roleUsers',
                title: t('roles'),
                render: (_, entity) => (
                    <Space wrap size={4}>
                        {entity.roleUsers?.map((ru) => (
                            <Tag bordered={false} key={ru.role_id}>
                                {ru.role.name}
                            </Tag>
                        ))}
                    </Space>
                ),
            },
            {
                key: 'region',
                title: t('region'),
                width: 200,
                render: (_, entity) => {
                    const { area, city, latitude, longitude, nickname, province, username } = entity;

                    const region = [province, city, area].join(' ').trim() || '-';

                    return latitude && longitude ? (
                        <a
                            href={`http://api.map.baidu.com/marker?location=${latitude},${longitude}&title=${nickname}(@${username})&content=${region}&output=html`}
                            target="_blank"
                        >
                            {region === '-' ? t('page.users.map') : region}
                        </a>
                    ) : (
                        region
                    );
                },
            },
            {
                dataIndex: 'status',
                title: t('status'),
                width: 200,
                render: (_, entity) => {
                    if (entity.is_deleted) {
                        return (
                            <Tag bordered={false} color="red">
                                {t('status_deleted')}
                            </Tag>
                        );
                    }

                    return (
                        <>
                            {entity.is_disabled ? (
                                <Tag bordered={false} color="red">
                                    {t('status_disabled')}
                                </Tag>
                            ) : (
                                <Tag bordered={false} color="green">
                                    {t('status_enabled')}
                                </Tag>
                            )}
                        </>
                    );
                },
            },
            {
                dataIndex: 'created_at',
                title: t('created_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                dataIndex: 'updated_at',
                title: t('updated_at'),
                valueType: 'dateTime',
                width: 200,
            },
            {
                fixed: 'right',
                key: 'option',
                title: t('actions'),
                valueType: 'option',
                width: 200,
                render: (_, entity) => [
                    <Link key="detail" to={`/system-management/users/${entity.id}`}>
                        {t('details')}
                    </Link>,
                    <a
                        key="update"
                        onClick={() => {
                            updateRef.current?.update(entity);
                        }}
                    >
                        {t('edit')}
                    </a>,
                    <Spin key="delete" size="small" spinning={loading[entity.id] === true}>
                        <Popconfirm
                            title={t('ays')}
                            onConfirm={() => {
                                setLoading({ [entity.id]: true });

                                (entity.is_deleted
                                    ? uUser({ id: entity.id, is_deleted: false, version: entity.version })
                                    : dUser(entity.id)
                                )
                                    .then(() => refetchUsers())
                                    .finally(() => {
                                        setLoading({
                                            [entity.id]: false,
                                        });
                                    });
                            }}
                        >
                            {entity.is_deleted ? (
                                <Typography.Link type="success">{t('restore')}</Typography.Link>
                            ) : (
                                <Typography.Link type="danger">{t('delete')}</Typography.Link>
                            )}
                        </Popconfirm>
                    </Spin>,
                ],
            },
        ],
        dataSource: get(dataUsers, 'data.data', []),
        loading: loadingUsers,
        options: {
            reload: () => refetchUsers(),
        },
        pagination: {
            ...pagination,
            total: get(dataUsers, 'data.meta.total', 0),
        },
        rowKey: 'id',
        scroll: {
            x: 1600,
        },
        search: false,
        toolbar: {
            actions: [<ComponentCreateUpdate key="create_update" ref={updateRef} onFinish={() => refetchUsers()} />],
        },
    };

    return (
        <>
            <ProCard className="mb-16">
                <Form {...formSearchProps}>
                    <Flex gap={16} wrap="wrap">
                        <Form.Item style={{ marginRight: 0 }}>
                            <Space.Compact>
                                <Form.Item name="key" noStyle>
                                    <Select
                                        options={[
                                            { label: 'ID', value: 'id' },
                                            { label: t('email'), value: 'email_fuzzy' },
                                            { label: t('mobile'), value: 'mobile_fuzzy' },
                                        ]}
                                        style={{ width: 100 }}
                                    />
                                </Form.Item>

                                <Form.Item name="value" noStyle>
                                    <Input allowClear={true} style={{ width: 200 }} />
                                </Form.Item>
                            </Space.Compact>
                        </Form.Item>

                        <Form.Item name="nickname_fuzzy" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('nickname')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="username_fuzzy" style={{ marginRight: 0 }}>
                            <Input allowClear={true} placeholder={t('username')} style={{ width: 200 }} />
                        </Form.Item>

                        <Form.Item name="is_disabled" style={{ marginRight: 0 }}>
                            <Select
                                allowClear={true}
                                options={[
                                    { label: t('status_enabled'), value: false },
                                    { label: t('status_disabled'), value: true },
                                ]}
                                placeholder={t('status')}
                                style={{ width: 100 }}
                            />
                        </Form.Item>

                        <Form.Item label={t('created_at')} name="created_at" style={{ marginRight: 0 }}>
                            <DatePicker.RangePicker allowClear={true} presets={RANGE_PICKER_PRESETS(t)} />
                        </Form.Item>

                        <Form.Item name="is_deleted" style={{ marginRight: 0 }} valuePropName="checked">
                            <Checkbox>{t('status_deleted')}</Checkbox>
                        </Form.Item>

                        <Button
                            loading={loadingUsers}
                            type="primary"
                            onClick={() => {
                                formSearch.submit();
                            }}
                        >
                            {t('search')}
                        </Button>

                        <Button
                            onClick={() => {
                                formSearch.resetFields();
                            }}
                        >
                            {t('reset')}
                        </Button>
                    </Flex>
                </Form>
            </ProCard>

            <ProTable {...proTableProps} />
        </>
    );
};

export default PageUsers;
